import React, { useEffect, useRef } from "react";
import { useMeasure } from "react-use";
import { useModal } from "@danishagro/shared/src/contexts/modal.context";
import { useModalMessage } from "@danishagro/shared/src/hooks/useModalMessage.hook";
import { useStickyBannerMessages } from "@danishagro/shared/src/hooks/useStickyBannerMessages.hook";
import { DA_HeaderMessage } from "@danishagro/shared/src/components/molecules/Message/HeaderMessage/headerMessage.component";
import { DA_ModalMessage } from "@danishagro/shared/src/components/molecules/Message/ModalMessage/modalMessage.component";
import { DA_Icon, DA_IconNames } from "@danishagro/shared/src/components/atoms/Icon/Icon.component";
import { useScreen } from "@danishagro/shared/src/contexts/screen.context";
import { useSiteHeader } from "@danishagro/shared/src/contexts/siteHeader.context";
import { useOnClickOutside } from "@danishagro/shared/src/hooks/useOnClickOutside.hook";
import { DA_Container } from "@danishagro/shared/src/components/atoms/Container/Container.component";
import { DA_BasicLink } from "@danishagro/shared/src/components/atoms/BasicLink/BasicLink.component";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { DA_ServiceMenu } from "@danishagro/shared/src/components/organisms/SiteHeader/ServiceMenu/ServiceMenu.component";
import { DA_NavigationDesktop } from "@danishagro/shared/src/components/organisms/SiteHeader/NavigationDesktop/NavigationDesktop.component";
import { DA_AccountPickerPopUp } from "@danishagro/shared/src/components/organisms/SiteHeader/AccountPicker/AccountPicker.component";
import { DA_NavigationMobileTrigger } from "@danishagro/shared/src/components/organisms/SiteHeader/NavigationMobile/components/Trigger/Trigger.component";
import { DA_LanguageSelector } from "@danishagro/shared/src/components/organisms/SiteHeader/LanguageSelector/LanguageSelector.component";
import DA_SearchInputB2c from "@danishagro/shared/src/components/organisms/SiteHeader/SearchInputB2c/SearchInputB2c.component";
import { DA_LinkProps } from "@danishagro/shared/src/interfaces/linkProps.interface";
import { ReactComponent as HattingAgroB2BLogo } from "@danishagro/shared/src/assets/svg/logos/Hatting.svg";
import { DA_ServiceButtonsB2B } from "@danishagro/shared/src/components/organisms/SiteHeader/ServiceButtonsB2B/ServiceButtonsB2B.component";
import { DA_ToolbarB2B } from "@organisms/ToolbarB2B/ToolbarB2B.component";
import { DA_MiniCartPopUp } from "./MiniCart/MiniCart.component";
import S from "./SiteHeader.module.scss";

export interface SiteHeaderProps {
    customerServiceLinks?: DA_LinkProps[];
}

export const B2C_SiteHeaderB2B = (props: SiteHeaderProps) => {
    const {
        setHeaderMeasure,
        showAccountPicker,
        showMiniCart,
        togglePopUp,
        toggleMobileNav,
        showMobileNav,
        showServiceMenu,
    } = useSiteHeader();
    const { getDictionaryString } = useTranslations();
    const { isAppFunctional } = useAppData();
    const { isMobileOrTablet } = useScreen();
    const [headerRef, headerRect] = useMeasure<HTMLDivElement>();
    const serviceButtonsRef = useRef<HTMLDivElement>();
    const popUpAreaRef = useRef<HTMLDivElement>();

    useOnClickOutside([serviceButtonsRef, popUpAreaRef], () => togglePopUp(undefined));

    useEffect(() => setHeaderMeasure(headerRect), [headerRect, setHeaderMeasure]);

    // Formats the name of the logo
    // const formatLogoName = (name: string) => name?.replace(/\s/g, "") || "";

    // TODO: SET FROM SCRIPT
    const messages = Array.isArray(globalThis?.announcementMessages)
        ? globalThis?.announcementMessages
        : null;

    // Determine which message to show
    const { currentMessage, handleModalClose } = useModalMessage(messages);
    const activeStickyBanners = useStickyBannerMessages(messages);
    const { showModal, isModalShowing, closeModal } = useModal();

    const getFrontPagePath = globalThis?.siteSettingsObj?.frontPagePath;
    const isLoginOptionsTrue = location.hostname !== "localhost" ? global?.apiGatewayUrl : true;

    // ExcellenceProgramme CMS
    const isExcellenceProgrammeCmsSite =
        typeof globalThis?.isUsingCustomAd === "string"
            ? globalThis.isUsingCustomAd.toLowerCase() === "true"
            : false;

    return (
        <>
            {/** Header */}
            <header ref={headerRef} className={S.header}>
                <a href="#main" className={S.skipToMainContentLink}>
                    {getDictionaryString("accessibilityMainContent")}
                </a>

                {!isMobileOrTablet &&
                    ((globalThis?.siteSettingsObj?.universalMenuLinks &&
                        globalThis.siteSettingsObj.universalMenuLinks.length > 0) ||
                        globalThis?.siteSettingsObj?.showLanguageSelector ||
                        isExcellenceProgrammeCmsSite ||
                        showServiceMenu) && (
                        <div className={S.serviceArea}>
                            <DA_Container noVerticalPadding>
                                <div className={S.serviceAreaFlex}>
                                    <DA_ServiceMenu />
                                    <div className={S.serviceRight}>
                                        {globalThis.siteSettingsObj?.showLanguageSelector && (
                                            <DA_LanguageSelector />
                                        )}
                                        {isAppFunctional &&
                                            isLoginOptionsTrue &&
                                            showServiceMenu &&
                                            !isMobileOrTablet && (
                                                <DA_ServiceButtonsB2B ref={serviceButtonsRef} />
                                            )}
                                    </div>
                                    {isExcellenceProgrammeCmsSite && (
                                        <div className={S.logOutLink}>
                                            <DA_BasicLink
                                                href="/logout"
                                                target="_self"
                                                conventionalRouting
                                                className={S.buttonLink}
                                            >
                                                <DA_Icon
                                                    name={DA_IconNames.SignOut}
                                                    className={S.buttonIcon}
                                                />
                                                <span className={S.buttonText}>
                                                    {getDictionaryString("logOut", {
                                                        uppercaseFirst: true,
                                                    })}
                                                </span>
                                            </DA_BasicLink>
                                        </div>
                                    )}
                                </div>
                            </DA_Container>
                        </div>
                    )}
                <DA_ModalMessage
                    currentMessage={currentMessage}
                    showModal={showModal}
                    isModalShowing={isModalShowing}
                    handleModalClose={handleModalClose}
                    closeModal={closeModal}
                    getDictionaryString={getDictionaryString}
                />
                {!isMobileOrTablet && (
                    <DA_HeaderMessage activeStickyBanners={activeStickyBanners} />
                )}

                <DA_Container noVerticalPadding>
                    <div className={S.navigationWrapper}>
                        <div className={S.logoWrapper}>
                            <DA_BasicLink
                                href={getFrontPagePath ? getFrontPagePath : "/"}
                                onClick={() => (showMobileNav ? toggleMobileNav() : null)}
                                ariaLabel={getDictionaryString("frontpage")}
                                className={S.logo}
                                forceNativeUrl={true}
                            >
                                <HattingAgroB2BLogo />
                            </DA_BasicLink>

                            {!isMobileOrTablet && (
                                <div className={S.search}>
                                    <DA_SearchInputB2c trapFocus smallHeight />
                                </div>
                            )}
                        </div>

                        {isMobileOrTablet ? (
                            <DA_NavigationMobileTrigger {...props} />
                        ) : (
                            <DA_NavigationDesktop />
                        )}

                        <div ref={popUpAreaRef}>
                            {/** Popup: Account Picker */}
                            {showAccountPicker && !isMobileOrTablet && <DA_AccountPickerPopUp />}

                            {/** Popup: Mini Cart */}
                            {/** Popup: Mini Cart */}
                            {showMiniCart && !isMobileOrTablet && <DA_MiniCartPopUp />}

                            {/* TODO: Detect that Account Picker or Mini Cart has gone from Desktop to MobileOrTablet and open the respective bottom sheet */}
                        </div>
                    </div>
                </DA_Container>
            </header>

            {/** Toolbar */}
            <DA_ToolbarB2B isLoginOptionsTrue={isLoginOptionsTrue} />
        </>
    );
};
